import { ApiError, apiErrorCode } from "@/lib/api";
import type { AuthedRequester } from "@/lib/brokerAuth";
import { messageFor } from "@/lib/errors";

// What Signal works for the broker on a submission (PMS-S870, PMS-S872,
// PMS-E044): a bind request, an underwriter review, a quote request, or
// Signal's request for information, each with where it stands and the events
// Signal shares with the broker. A quote's Actions tab reads its own
// (BRK-S465); the submission's Actions tab reads them all (BRK-S464).

/** Where an action item stands, as PMS sends it. */
export type ActionStatus =
  | "open"
  | "in_progress"
  | "waiting_on_carrier"
  | "waiting_on_broker"
  | "done"
  | "cancelled";
export const ACTION_STATUSES: readonly ActionStatus[] = [
  "open",
  "in_progress",
  "waiting_on_carrier",
  "waiting_on_broker",
  "done",
  "cancelled",
];

export type ActionKind =
  | "referral_review"
  | "bind_request"
  | "quote_request"
  | "info_request";

export type ActionEventType =
  | "opened"
  | "status"
  | "assigned"
  | "note"
  | "info_request"
  | "broker_reply"
  | "closed";

export interface ActionDocument {
  id: string;
  filename: string;
}

/** One broker-visible event on an item. Signal's author name is always
 *  "Signal Specialty". */
export interface ActionEvent {
  id: string;
  at: string;
  type: ActionEventType;
  body: string | null;
  author: { kind: "broker" | "signal"; name: string };
  documents: ActionDocument[];
}

/** Signal's latest request for information, while the item waits on the
 *  broker and no reply has followed it. */
export interface OpenRequest {
  eventId: string;
  message: string;
  at: string;
}

/** PMS's `BrokerActionItem`. */
export interface ActionItem {
  id: string;
  kind: ActionKind;
  status: ActionStatus;
  title: string;
  quoteId: string | null;
  openedAt: string;
  updatedAt: string;
  closedAt: string | null;
  openRequest: OpenRequest | null;
  /** Oldest first. */
  events: ActionEvent[];
}

/** Where a request the broker (or Signal) opened stands, as a quote's or a
 *  carrier's view carries it: a review (PMS-S871) or a manual carrier's
 *  quote request (PMS-S819). */
export interface ActionStanding {
  actionId: string;
  status: ActionStatus;
  requestedAt: string;
}

/** PMS's action item, read for its standing; null when it isn't one. */
export function readActionStanding(value: unknown): ActionStanding | null {
  if (typeof value !== "object" || value === null) return null;
  const { id, actionId, status, openedAt, requestedAt } = value as {
    id?: unknown;
    actionId?: unknown;
    status?: unknown;
    openedAt?: unknown;
    requestedAt?: unknown;
  };
  // The item itself names `id` and `openedAt`; a view's standing names
  // `actionId` and `requestedAt`.
  const theId = typeof id === "string" ? id : actionId;
  const at = typeof openedAt === "string" ? openedAt : requestedAt;
  if (typeof theId !== "string" || typeof at !== "string") return null;
  const known = ACTION_STATUSES.find((s) => s === status);
  return known ? { actionId: theId, status: known, requestedAt: at } : null;
}

/** A refused request that opens an action (a review, a manual quote), in
 *  words to act on: `network` may have reached Signal (retry with the same
 *  key); `settled` means it moved on, so the page reads again; `key_reused`
 *  means PMS has the key for another request, a slip of ours, so a fresh key
 *  is sent, as bind does; `refused` is anything else. */
export type SignalRequestRefusal = {
  kind: "network" | "settled" | "key_reused" | "refused";
  message: string;
};

export const ACTION_KIND_LABELS: Readonly<Record<ActionKind, string>> = {
  referral_review: "Underwriter review",
  bind_request: "Bind request",
  quote_request: "Quote request",
  info_request: "Information request",
};

export const ACTION_STATUS_LABELS: Readonly<Record<ActionStatus, string>> = {
  open: "With Signal",
  in_progress: "Signal is working on it",
  waiting_on_carrier: "With the carrier",
  waiting_on_broker: "Signal needs something from you",
  done: "Done",
  cancelled: "Closed",
};

export const ACTION_EVENT_LABELS: Readonly<Record<ActionEventType, string>> = {
  opened: "Opened",
  status: "Status changed",
  assigned: "Assigned",
  note: "Note",
  info_request: "Signal asked for more information",
  broker_reply: "Reply",
  closed: "Closed",
};

/** A word for a value a newer PMS may send that this build doesn't know. */
function wordOf(
  words: Readonly<Record<string, string | undefined>>,
  key: string,
): string | undefined {
  return Object.hasOwn(words, key) ? words[key] : undefined;
}

/** The item's kind in words, else PMS's own title for it. */
export function actionKindLabel(item: Pick<ActionItem, "kind" | "title">) {
  return wordOf(ACTION_KIND_LABELS, item.kind) ?? item.title;
}

/** Where the item stands in words; an unknown status reads as being worked. */
export function actionStatusLabel(status: string): string {
  return wordOf(ACTION_STATUS_LABELS, status) ?? "Signal is working on it";
}

/** What an event was, in words; an unknown one reads as an update. */
export function actionEventLabel(type: string): string {
  return wordOf(ACTION_EVENT_LABELS, type) ?? "Update";
}

/** GET …/submissions/{id}/actions: the submission's items, most recently
 *  updated first, closed ones too. Null for a PMS before PMS-S872 (404),
 *  which has none to show. */
export async function listActionItems(
  request: AuthedRequester,
  submissionId: string,
): Promise<ActionItem[] | null> {
  try {
    const { items } = await request<{ items: ActionItem[] }>(
      `/api/broker/submissions/${encodeURIComponent(submissionId)}/actions`,
    );
    return items;
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** An item's element id on the page, which a deep link scrolls to. */
export function actionAnchor(actionId: string): string {
  return `action-${actionId}`;
}

/** A quote's own items, in PMS's order. */
export function actionItemsOf(
  items: readonly ActionItem[],
  quoteId: string,
): ActionItem[] {
  return items.filter((item) => item.quoteId === quoteId);
}

/** What Signal waits on first, then the rest in PMS's order (BRK-S464). */
export function forBroker(items: readonly ActionItem[]): ActionItem[] {
  return [
    ...items.filter((item) => item.openRequest),
    ...items.filter((item) => !item.openRequest),
  ];
}

/** How many items wait on a reply from the broker. */
export function waitingOnBroker(items: readonly ActionItem[]): number {
  return items.filter((item) => item.openRequest).length;
}

/** The longest reply PMS takes. */
export const REPLY_MAX = 4000;

/** Why a reply can't go yet, or null. */
export function replyError(body: string): string | null {
  if (!body.trim()) return "Write your reply first.";
  if (body.length > REPLY_MAX)
    return `Keep the reply to ${REPLY_MAX.toLocaleString("en-US")} characters or fewer.`;
  return null;
}

/** POST …/actions/{actionId}/replies (PMS-S872): the broker's answer to
 *  Signal's request, with the submission's documents it names. PMS takes no
 *  Idempotency-Key, so the caller guards a second click. */
export async function replyToAction(
  request: AuthedRequester,
  submissionId: string,
  actionId: string,
  input: { body: string; documentIds: readonly string[] },
): Promise<ActionItem> {
  const answer = await request<ActionItem | undefined>(
    `/api/broker/submissions/${encodeURIComponent(submissionId)}/actions/${encodeURIComponent(actionId)}/replies`,
    {
      method: "POST",
      body: {
        body: input.body.trim(),
        ...(input.documentIds.length > 0
          ? { documentIds: [...input.documentIds] }
          : {}),
      },
    },
  );
  if (!answer?.id) {
    throw new ApiError(
      200,
      "Signal answered, but not with the request. Reload to check.",
    );
  }
  return answer;
}

/** A refused reply, in words to act on: `network` may have reached Signal,
 *  and with no key a second send could make two, so the actions are read
 *  again first; `settled` means the request moved on, so they're read again
 *  too; `refused` keeps the reply to fix and send. */
export type ReplyRefusal = {
  kind: "network" | "settled" | "refused";
  message: string;
};

export function replyRefusalOf(err: unknown): ReplyRefusal {
  if (err instanceof ApiError && err.status === 0) {
    return {
      kind: "network",
      message:
        "We couldn’t confirm your reply reached Signal. Check the request below before sending it again.",
    };
  }
  switch (apiErrorCode(err)) {
    case "action_closed":
      return {
        kind: "settled",
        message: "Signal has closed this request, so it can’t take a reply.",
      };
    case "document_not_on_submission":
      return {
        kind: "refused",
        message:
          "One of the files isn’t on this submission any more. Remove it and send again.",
      };
    default:
      return { kind: "refused", message: messageFor(err) };
  }
}
